import { Injectable, inject } from '@angular/core';
import { HttpClientService } from './http.service';

interface RouteResponse {
  geometry: [number, number][];
  distance: number;
  duration: number;
}

interface RouteCache {
  result: RouteResponse;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class OsrmService {
  private http = inject(HttpClientService);

  private routeCache = new Map<string, RouteCache>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  constructor() {
    // Limpiar cache cada 10 minutos
    setInterval(() => this.cleanExpiredCache(), 10 * 60 * 1000);
  }

  /**
   * Obtiene la ruta entre dos puntos usando OSRM
   * @param from Punto de origen [lng, lat]
   * @param to Punto de destino [lng, lat]
   * @returns Respuesta de OSRM con la geometría de la ruta
   */
  async getRoute(
    from: [number, number],
    to: [number, number],
  ): Promise<RouteResponse | null> {
    // Generar clave de cache basada en coordenadas redondeadas
    const cacheKey = this.getCacheKey(from, to);

    // Verificar si existe en cache y no está expirado
    const cached = this.routeCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.result;
    }

    try {
      const result = await this.http.post<RouteResponse>('/osrm/route', {
        from,
        to,
      });

      // Guardar en cache
      this.routeCache.set(cacheKey, {
        result,
        timestamp: Date.now(),
      });

      return result;
    } catch (error) {
      console.error('Error obteniendo ruta OSRM:', error);
      return null;
    }
  }

  /**
   * Genera una clave de cache basada en las coordenadas
   * Redondea las coordenadas a 5 decimales para agrupar puntos cercanos
   */
  private getCacheKey(from: [number, number], to: [number, number]): string {
    const fromRounded = from.map((c) => c.toFixed(5)).join(',');
    const toRounded = to.map((c) => c.toFixed(5)).join(',');
    return `${fromRounded}_${toRounded}`;
  }

  /**
   * Limpia entradas del cache que han expirado
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.routeCache.forEach((value, key) => {
      if (now - value.timestamp > this.CACHE_TTL) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.routeCache.delete(key));
  }

  /**
   * Limpia todo el cache manualmente
   */
  clearCache(): void {
    this.routeCache.clear();
  }
}
